// ST-1258 -Claude- Language picker shows English visibly and native script in menu.
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  SUPPORTED_LANGUAGES,
  getLanguageInfo,
  normalizeLanguageCode,
} from '@nearrme/i18n';
import { persistLanguagePreference } from '../i18n';

export function LanguagePicker() {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [currentCode, setCurrentCode] = useState(() =>
    normalizeLanguageCode(i18n.language, i18n.resolvedLanguage)
  );

  useEffect(() => {
    const syncCurrentLanguage = (language?: string) => {
      setCurrentCode(normalizeLanguageCode(language, i18n.language, i18n.resolvedLanguage));
    };

    syncCurrentLanguage();
    i18n.on('languageChanged', syncCurrentLanguage);
    i18n.on('initialized', syncCurrentLanguage);

    return () => {
      i18n.off('languageChanged', syncCurrentLanguage);
      i18n.off('initialized', syncCurrentLanguage);
    };
  }, [i18n]);

  const current = getLanguageInfo(currentCode) ?? SUPPORTED_LANGUAGES[0];

  const selectLanguage = async (language: string) => {
    const nextCode = persistLanguagePreference(language);
    setCurrentCode(nextCode);
    await i18n.changeLanguage(nextCode);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
        aria-label="Language"
      >
        <span className="h-6 w-6 rounded-full bg-slate-900 dark:bg-white text-white dark:text-slate-950 flex items-center justify-center text-[10px] font-black">
          EN
        </span>
        <span className="text-slate-700 dark:text-slate-300">
          {current.english === 'English' ? 'English' : current.native}
        </span>
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-30"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <div className="absolute right-0 top-full mt-2 w-56 max-h-80 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 shadow-lg z-40">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <button
                key={lang.code}
                type="button"
                onClick={() => {
                  void selectLanguage(lang.code);
                }}
                aria-current={lang.code === currentCode ? 'true' : undefined}
                className={`w-full text-left px-4 py-2 text-sm hover:bg-slate-100 dark:hover:bg-slate-800 ${
                  lang.code === currentCode ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300' : ''
                }`}
              >
                <span className="font-medium">{lang.english}</span>
                <span className="text-xs text-slate-500 dark:text-slate-400 ml-2">{lang.native}</span>
                {!lang.bundled && <span className="text-xs ml-2">Soon</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
